import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  OnInit,
  ViewChild,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { ProductService } from '../../core/services/product.service';
import { CategoryService } from '../../core/services/category.service';
import { UnitOfMeasureService } from '../../core/services/unit-of-measure.service';
import { StoreService } from '../../core/services/store.service';
import { InventoryService } from '../../core/services/inventory.service';
import { BillingService } from '../../core/services/billing.service';
import {
  ProductDto,
  ProductLiteDto,
  CategoryDto,
  UnitOfMeasureDto,
  CreateProductRequest,
  UpdateProductRequest,
  TransferRequest,
  AdjustStockRequest,
  ProductsStockSummaryDto,
} from '../../core/models/product.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import {
  CreateProductWithVariantsRequest,
  ProductVariantDto,
  ProductVariantMatrixDto,
} from '../../core/models/variant.models';
import { BarcodeScannerComponent } from '../../shared/components/barcode-scanner/barcode-scanner.component';
import { ProductDetailComponent } from '../../shared/components/product-detail/product-detail.component';
import {
  VariantBuilderComponent,
  VariantCombo,
} from '../../shared/components/variant-builder/variant-builder.component';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

interface ProductForm {
  name: string;
  brand: string;
  barcode: string;
  purchasePrice: number;
  sellPrice: number;
  initialQuantity: number;
  storeId: string | null;
  categoryId: string | null;
  unitOfMeasureId: string | null;
  isWeight: boolean;
  /** Sotuv valyutasi — sellPrice va discount shu valyutada. */
  sellCurrency: string;
  /** Kelish valyutasi — purchasePrice shu valyutada. Sotuvdan farq qilishi mumkin. */
  purchaseCurrency: string;
  /**
   * Tahrirda qoldiq o'zgartirilsa — majburiy sabab. Qoldiq "tuzatish" (Adjustment)
   * sifatida yoziladi, kirim/chiqim sifatida emas, shuning uchun audit izohi kerak.
   */
  adjustReason: string;
}

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    ConfirmDialogModule,
    TooltipModule,
    RadioButtonModule,
    CheckboxModule,
    BarcodeScannerComponent,
    ProductDetailComponent,
    VariantBuilderComponent,
    TranslatePipe,
    MultiSelectModule,
    DrawerModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './products.component.html',
})
export class ProductsComponent implements OnInit {
  @ViewChild('cameraPreview') private cameraPreview?: ElementRef<HTMLVideoElement>;

  private productSvc = inject(ProductService);
  readonly currencySvc = inject(CurrencyService);
  private categorySvc = inject(CategoryService);
  private unitSvc = inject(UnitOfMeasureService);
  private storeSvc = inject(StoreService);
  private inventorySvc = inject(InventoryService);
  private confirmSvc = inject(ConfirmationService);
  private authSvc = inject(AuthService);
  private billingSvc = inject(BillingService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Do'konlar ro'yxati forma ochilgandan KEYIN kelishi mumkin (birinchi yuklash
    // hali tugamagan bo'lsa). Shunda ham yagona do'kon o'zi tanlanib qolsin —
    // `openCreate()` dagi darhol qo'yish faqat ro'yxat tayyor holatni qoplaydi.
    effect(() => {
      const only = this.formStore.single();
      if (!only || this.formStoreId() === only.id) return;

      // Tahrirlashda ATAYLAB tegilmaydi: u yerda `formStoreId` mahsulotning o'z
      // do'koni bo'lib turadi va uni almashtirish `onFormStoreChange` orqali
      // kategoriya va o'lchov birligini tozalab yuborardi.
      if (this.isEdit) return;

      this.onFormStoreChange(only.id);
    });

    this.destroyRef.onDestroy(() => this.stopCamera());
  }

  onFormStoreChange(storeId: string | null) {
    this.form.storeId = storeId;
    this.formStoreId.set(storeId);
    if (
      this.form.categoryId &&
      !this.filteredCategories().find((c) => c.id === this.form.categoryId)
    ) {
      this.form.categoryId = null;
    }
    if (
      this.form.unitOfMeasureId &&
      !this.filteredUnits().find((u) => u.id === this.form.unitOfMeasureId)
    ) {
      this.form.unitOfMeasureId = null;
    }
  }

  onProductCategoryChange(categoryId: string | null) {
    this.form.categoryId = categoryId;
  }

  /** "Variantli mahsulot" checkbox almashtirilganda. Yoqilganda variant qurish drawerini ochamiz. */
  setVariantMode(on: boolean) {
    this.variantMode.set(on);
    if (on) {
      this.variantDrawerVisible = true;
    } else {
      this.variantDrawerVisible = false;
      this.combos.set([]);
    }
  }

  /** variant-builder kombinatsiyalarni yangiladi. */
  onCombosChange(combos: VariantCombo[]) {
    this.combos.set(combos);
  }

  allowProductImages = computed(() => this.billingSvc.hasModule('product_images'));

  // ── Table state ──────────────────────────────────────────────────────────────
  products = signal<ProductDto[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  pageSize = 20;

  // ── Jamlanma kartochkalar (joriy qoldiq bo'yicha) ────────────────────────────
  /** Ro'yxat filtri (qidiruv + do'konlar) bilan bir xil filtrlanadi. */
  summary = signal<ProductsStockSummaryDto | null>(null);
  summaryLoading = signal(false);

  // ── Filters ──────────────────────────────────────────────────────────────────
  /**
   * Inputdagi matn — `searchQuery` dan ATAYIN ajratilgan. Enter bosilganda input
   * tozalanadi, lekin ro'yxat o'sha so'rov natijasi bo'lib qolaveradi.
   */
  searchText = '';
  searchQuery = '';
  filterStoreIds: string[] = [];

  private readonly searchSubject = new Subject<string>();

  /**
   * Ekrandagi ro'yxat QAYSI matn natijasi ekani. Qidiruv 300 ms kechikish bilan
   * ishlagani uchun bu `searchQuery` dan orqada qolishi mumkin — `onSearchEnter`
   * shuni tekshiradi.
   */
  private loadedQuery: string | null = null;

  /**
   * Serverga OXIRGI yuborilgan matn — javob kelishini kutmasdan, so'rov boshlanishida
   * yoziladi. `loadedQuery` bundan farq qiladi: u javob KELGANDAN keyin yangilanadi.
   * Ikkovi kerak, chunki debounce hali javob yo'lda ekan ham ishlab ketishi mumkin.
   */
  private requestedQuery: string | null = null;

  // ── Lookup data ──────────────────────────────────────────────────────────────
  categories = signal<CategoryDto[]>([]);
  units = signal<UnitOfMeasureDto[]>([]);
  stores = signal<StoreLiteDto[]>([]);

  // ── Form store filter (create form) ──────────────────────────────────────────
  formStoreId = signal<string | null>(null);

  /** Yaratish formasidagi do'kon tanlagichi (qoida uchun `storePicker` ga qarang). */
  readonly formStore = storePicker(this.stores);

  /**
   * Ro'yxat filtri. Bitta do'konda ko'rsatilmaydi va qiymat ham QO'YILMAYDI —
   * bo'sh filtr "hammasi" degani, bu esa yagona do'kon bilan bir xil natija.
   */
  readonly storeFilterVisible = this.formStore.visible;

  /**
   * Ko'chirish tugmasi ko'rinadimi. Ko'chirish — bir do'kondan ikkinchisiga
   * o'tkazish, ya'ni kamida ikkita do'kon bo'lmasa amalning ma'nosi yo'q.
   */
  readonly canTransfer = computed(() => this.stores().length > 1);
  formStoreTypeId = computed(() => {
    const id = this.formStoreId();
    if (!id) return null;
    return this.stores().find((s) => s.id === id)?.storeTypeId ?? null;
  });
  filteredCategories = computed(() => {
    const typeId = this.formStoreTypeId();
    if (!typeId) return this.categories();
    return this.categories().filter((c) => c.storeTypeId === typeId);
  });
  filteredUnits = computed(() => {
    const typeId = this.formStoreTypeId();
    if (!typeId) return this.units();
    return this.units().filter((u) => u.storeTypeId === typeId);
  });

  // ── Variant builder (yaratish dialogida) ─────────────────────────────────────
  /** Foydalanuvchi yoqgan "variantli mahsulot" rejimi. */
  variantMode = signal(false);
  /** variant-builder'dan kelgan kombinatsiyalar. */
  combos = signal<VariantCombo[]>([]);
  /** O'ng tomondan chiqadigan variant qurish drawer ko'rinishi. */
  variantDrawerVisible = false;
  /** Barcha kombinatsiyalardagi jami dona soni (xulosa uchun). */
  totalVariantQty = computed(() => this.combos().reduce((s, c) => s + (c.quantity || 0), 0));

  // ── Product dialog ───────────────────────────────────────────────────────────
  dialogVisible = false;
  saving = signal(false);
  editingId: string | null = null;
  get isEdit(): boolean {
    return !!this.editingId;
  }

  // ── Variant boshqaruvchi (edit rejimida) ─────────────────────────────────────
  /** Edit drawer ko'rinishi (create'dan alohida — initialMatrix bilan ishlaydi). */
  editVariantDrawerVisible = false;
  editVariantMatrix = signal<ProductVariantMatrixDto | null>(null);
  editVariantLoading = signal(false);
  editVariantSaving = signal(false);
  /** Edit drawer'dan kelgan kombinatsiyalar. */
  editCombos = signal<VariantCombo[]>([]);
  editAttributeIds: string[] = [];
  editingHasVariants = false;
  /** Variant drawer'ida qoldiq o'zgartirilsa — majburiy tuzatish sababi. */
  variantAdjustReason = '';
  /** Shablon valyuta tanlagichini qulflash uchun o'qiydi. */
  originalEditQuantity = 0;

  /** Tahrirda (variantsiz) qoldiq o'zgartirildimi — sabab maydonini ko'rsatish uchun. */
  get editQuantityChanged(): boolean {
    return (
      !!this.editingId &&
      !this.editingHasVariants &&
      this.form.initialQuantity !== this.originalEditQuantity
    );
  }

  /** Variant drawer'ida mavjud variantlardan birortasining qoldig'i o'zgartirildimi. */
  get variantQuantityChanged(): boolean {
    return this.editCombos().some((c) => c.variantId && c.quantity !== (c.currentStock ?? 0));
  }

  /** Edit: variantlarni boshqarish drawerini ochadi (matritsa yuklab). */
  openVariantsManager(): void {
    if (!this.editingId) return;
    const pid = this.editingId;
    this.editVariantMatrix.set(null);
    this.editCombos.set([]);
    this.editAttributeIds = [];
    this.editVariantLoading.set(true);
    this.editVariantDrawerVisible = true;
    this.productSvc
      .getVariantMatrix(pid, this.formStoreId() ?? undefined)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.editVariantLoading.set(false)),
      )
      .subscribe({
        next: (res) => this.editVariantMatrix.set(res.data ?? null),
        error: () => this.editVariantMatrix.set(null),
      });
  }

  onEditCombosChange(combos: VariantCombo[]) {
    this.editCombos.set(combos);
  }

  /** Edit: drawer'dagi kombinatsiyalarni manageVariants bilan saqlaydi. */
  saveEditVariants(): void {
    const pid = this.editingId;
    if (!pid) return;
    const combos = this.editCombos();
    if (combos.length === 0) {
      this.productSvc.showWarn(
        "Variant qiymatlarini tanlang yoki kamida bitta kombinatsiya bo'lsin",
      );
      return;
    }
    if (!this.form.storeId) {
      this.productSvc.showWarn("Miqdorni tahrirlash uchun mahsulot do'koni aniqlanishi kerak");
      return;
    }
    // Mavjud variantlarning qoldig'i o'zgargan bo'lsa — tuzatish sababi majburiy.
    const qtyChanged = combos.some((c) => c.variantId && c.quantity !== (c.currentStock ?? 0));
    if (qtyChanged && !this.variantAdjustReason.trim()) {
      this.productSvc.showWarn(this.translate.instant('PRODUCTS.FORM.ADJUST_REASON_REQUIRED'));
      return;
    }
    const adjustReason = this.variantAdjustReason.trim();
    this.editVariantSaving.set(true);
    this.productSvc
      .manageVariants(pid, {
        attributeIds: this.editAttributeIds.length ? this.editAttributeIds : null,
        // Miqdor edit'da boshqarilmaydi — u faqat "Mahsulot qo'shish" (Add Stock)
        // orqali kiritiladi. Bu yerda faqat variant turlari va narxlari.
        variants: combos.map((c) => ({
          id: c.variantId,
          valueIds: c.valueIds,
          sellPrice: c.price,
          purchasePrice: c.purchasePrice,
          ...(!c.variantId && {
            storeId: this.form.storeId,
            initialQuantity: c.quantity,
          }),
        })),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.editVariantSaving.set(false)),
      )
      .subscribe({
        next: () => {
          const stockUpdates = combos
            .filter((c) => c.variantId && c.quantity !== (c.currentStock ?? 0))
            .map((c) =>
              this.adjustStockTo(
                pid,
                this.form.storeId!,
                c.quantity,
                adjustReason,
                c.currentStock ?? 0,
                c.variantId!,
              ),
            );
          forkJoin(stockUpdates.length ? stockUpdates : [of(null)])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.productSvc.showUpdateSuccess();
                this.editVariantDrawerVisible = false;
                this.reload();
              },
              error: (err) => this.productSvc.showUpdateError(err),
            });
        },
        error: (err) => this.productSvc.showUpdateError(err),
      });
  }

  form: ProductForm = this.emptyForm();

  // ── Discount UI state (separate from API discount field) ─────────────────────
  discountType: '%' | 'sum' = '%';
  discountInput = 0;
  markupPercent = 0;

  /**
   * Chegirmadan keyingi narx.
   *
   * `Math.round()` ISHLATILMAYDI: u butun songa yaxlitlardi, ya'ni `5.5 $` → `6 $`,
   * `0.02 $` → `0 $`. Narx valyuta aniqligida yaxlitlanadi (`roundPrice`).
   * Chegirma narxdan katta bo'lsa 0 da to'xtaydi — manfiy narx bo'lmaydi.
   */
  get finalPrice(): number {
    const s = this.form.sellPrice || 0;
    const raw =
      this.discountType === '%'
        ? s * (1 - (this.discountInput || 0) / 100)
        : s - (this.discountInput || 0);
    return this.currencySvc.roundPrice(Math.max(0, raw), this.form.sellCurrency);
  }

  /**
   * Bir donadagi foyda.
   *
   * Xarid va sotish valyutalari HAR XIL bo'lsa `null` — ikki valyutani ayirish
   * mumkin emas (loyihaning asosiy qoidasi: valyutalar hech qachon qo'shilmaydi
   * va ayirilmaydi). Bunday holatda shablon foyda o'rniga izoh ko'rsatadi.
   */
  get profit(): number | null {
    if (!this.profitComparable) return null;
    return this.currencySvc.roundPrice(
      this.finalPrice - (this.form.purchasePrice || 0),
      this.form.sellCurrency,
    );
  }

  /** Foyda/ustama hisoblash mumkinmi — faqat bitta valyuta ichida. */
  get profitComparable(): boolean {
    return this.form.purchaseCurrency === this.form.sellCurrency;
  }

  // ── Image upload ─────────────────────────────────────────────────────────────
  imageFile: File | null = null;
  imagePreview = signal<string | null>(null);
  imageError = signal<string | null>(null);
  imageUploading = signal(false);
  cameraOpen = signal(false);
  private cameraStream: MediaStream | null = null;
  /** Existing server image URL captured when edit dialog opens; used to know whether to call deleteImage(). */
  private originalImageUrl: string | null = null;
  /** True when user explicitly removed a server-side image (no new file selected). */
  removeExistingImage = false;

  // ── Inline category creation ─────────────────────────────────────────────────
  showNewCat = false;
  newCatName = '';
  savingCat = signal(false);

  // ── Inline unit creation ─────────────────────────────────────────────────────
  showNewUnit = false;
  newUnitName = '';
  savingUnit = signal(false);

  // ── Transfer dialog ──────────────────────────────────────────────────────────
  transferDialogVisible = false;
  transferSaving = signal(false);
  allProducts = signal<ProductLiteDto[]>([]);
  allProductsLoading = signal(false);

  transferForm: {
    productId: string | null;
    fromStoreId: string | null;
    toStoreId: string | null;
    quantity: number;
    description: string;
    productVariantId: string | null;
  } = this.emptyTransferForm();

  transferProductId = signal<string | null>(null);
  transferFromStoreId = signal<string | null>(null);
  transferToStoreId = signal<string | null>(null);
  transferStoreProducts = signal<ProductLiteDto[]>([]);
  transferStoreProductsLoading = signal(false);
  transferVariants = signal<ProductVariantDto[]>([]);
  transferVariantsLoading = signal(false);

  // toStore: fromStore bilan bir xil storeTypeId, fromStore o'zi chiqmasin
  transferToStoreOptions = computed(() => {
    const fromId = this.transferFromStoreId();
    if (!fromId) return [];
    const fromStore = this.stores().find((s) => s.id === fromId);
    return this.stores().filter(
      (s) =>
        s.id !== fromId &&
        (!fromStore?.storeTypeId || !s.storeTypeId || s.storeTypeId === fromStore.storeTypeId),
    );
  });

  // ── Add Stock dialog ─────────────────────────────────────────────────────────
  stockDialogVisible = false;
  stockSaving = signal(false);
  stockAdvanced = false;

  stockForm: {
    productId: string | null;
    storeId: string | null;
    quantity: number;
    productVariantId: string | null;
    newPurchasePrice: number | null;
    newSellPrice: number | null;
    description: string;
  } = this.emptyStockForm();

  /** Stok dialog'da tanlangan mahsulot (signal — computed filtr uchun). */
  stockProductId = signal<string | null>(null);
  private stockProductStoreTypeId = computed(() => {
    const pid = this.stockProductId();
    if (!pid) return null;
    return this.allProducts().find((p) => p.id === pid)?.storeTypeId ?? null;
  });

  /**
   * Stok oynasidagi narx maydonlari SHU mahsulot valyutasida kiritiladi — kiritish
   * aniqligi ham o'sha valyutadan olinadi, aks holda dollarli mahsulot so'm xonalari
   * bilan kesilib qolardi.
   */
  stockPurchaseCurrency = computed(
    () =>
      this.allProducts().find((p) => p.id === this.stockProductId())?.purchaseCurrency ??
      this.currencySvc.defaultCode(),
  );
  stockSellCurrency = computed(
    () =>
      this.allProducts().find((p) => p.id === this.stockProductId())?.sellCurrency ??
      this.currencySvc.defaultCode(),
  );
  filteredStockStores = computed(() => {
    const typeId = this.stockProductStoreTypeId();
    if (!typeId) return this.stores();
    return this.stores().filter((s) => s.storeTypeId === typeId);
  });

  /**
   * Stok oynasidagi tanlagich — BUTUN ro'yxat emas, mahsulot turiga mos do'konlar
   * ustidan. Uchta do'konli biznesda mahsulot faqat bittasiga to'g'ri kelsa, tanlash
   * baribir yagona bo'ladi.
   */
  readonly stockStore = storePicker(this.filteredStockStores);

  /** Stok dialog'da tanlangan mahsulotning variantlari. */
  stockVariants = signal<ProductVariantDto[]>([]);
  stockVariantsLoading = signal(false);

  private lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };
  private storesLoaded = false;

  ngOnInit() {
    forkJoin({
      cats: this.categorySvc.getAll(undefined, true),
      units: this.unitSvc.getAll(),
      stores: this.storeSvc.getAllLite(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ cats, units, stores }) => {
          this.categories.set(cats.data ?? []);
          this.units.set(units.data ?? []);
          const storeList = stores.data ?? [];
          this.stores.set(storeList);
          if (this.authSvc.user()?.role === 'Employee' && storeList.length > 0) {
            this.filterStoreIds = [storeList[0].id];
          }
          this.storesLoaded = true;
          this.loadProducts(this.lastLazyEvent);
        },
      });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // Skaner barkodni ~80 ms da yozib, DARHOL Enter yuboradi — ya'ni Enter
        // debounce'dan oldin ishlab, so'rovni allaqachon jo'natgan bo'ladi. Bu
        // tekshiruvsiz 300 ms keyin aynan o'sha matn bilan ikkinchi bir xil
        // so'rov ketardi.
        if (this.requestedQuery === value.trim()) return;
        this.onSearch();
      });
  }

  /**
   * @param onLoaded natijalar kelgach chaqiriladi — Enter bosilganda ro'yxat
   *   yangilanishini kutish uchun (pastdagi `onSearchEnter` ga qarang).
   */
  loadProducts(event: TableLazyLoadEvent, onLoaded?: () => void) {
    this.lastLazyEvent = event;
    if (!this.storesLoaded) return;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    const size = event.rows ?? this.pageSize;
    const query = this.searchQuery.trim();
    this.requestedQuery = query;
    this.loading.set(true);

    // Ro'yxat valyuta bo'yicha FILTRLANMAYDI — hamma mahsulot ko'rinishi kerak.
    // Valyuta faqat jamlanma kartochkalariga tegishli (`loadSummary`).
    this.productSvc
      .getProducts(
        page,
        size,
        this.searchQuery || undefined,
        this.filterStoreIds.length ? this.filterStoreIds : undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.products.set(res.data?.items ?? []);
          this.totalCount.set(res.data?.totalCount ?? 0);
          this.loadedQuery = query;
          onLoaded?.();
        },
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });

    this.loadSummary();
  }

  /**
   * Jamlanma kartochkalarida ko'rsatiladigan valyuta. Kartochkalar BITTA valyutadagi
   * mahsulotlarni jamlaydi — `$100` va `100 000 so'm` ni qo'shib bo'lmaydi.
   */
  summaryCurrency = signal<string>('');

  onSummaryCurrencyChange(code: string): void {
    if (code === this.summaryCurrency()) return;
    this.summaryCurrency.set(code);
    this.loadSummary();
  }

  /** Jamlanma kartochkalar — ro'yxat bilan AYNAN bir xil filtr uzatiladi. */
  private loadSummary() {
    this.summaryLoading.set(true);
    this.productSvc
      .getStockSummary(
        this.searchQuery || undefined,
        this.filterStoreIds.length ? this.filterStoreIds : undefined,
        this.summaryCurrency() || undefined,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.summaryLoading.set(false)),
      )
      .subscribe({
        next: (res) => {
          this.summary.set(res.data ?? null);
          // Backend qaysi valyutani qo'llaganini aytadi — tanlagich shunga
          // moslashadi. Busiz tanlagich bo'sh turardi va nima ko'rsatilayotgani
          // noaniq bo'lardi.
          if (res.data?.currency) this.summaryCurrency.set(res.data.currency);
        },
        error: () => this.summary.set(null),
      });
  }

  onSearch() {
    this.loadProducts({ first: 0, rows: this.pageSize });
  }

  /** Har bosishda emas, 300 ms jimlikdan keyin qidiradi — qo'lda yozish uchun. */
  onSearchInput(value: string) {
    this.searchText = value;
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  /**
   * Enter bosilganda qidiruvni DARHOL bajaradi va inputni tozalaydi.
   *
   * <p>
   * Qidiruv 300 ms kechikish bilan ishlaydi, simli skaner esa barkodni yozib
   * darhol Enter yuboradi — o'sha paytda ro'yxatda hali oldingi natijalar
   * turadi. Shuning uchun ro'yxat aynan shu matn natijasi ekaniga ishonch hosil
   * qilinadi; aks holda input eski ro'yxat ustida tozalanib, foydalanuvchi
   * butunlay boshqa mahsulotni ko'rib turgan bo'lardi.
   * </p>
   *
   * <p>
   * Input tozalanadi, `searchQuery` esa qoladi: keyingi skanerlash bo'sh maydonga
   * yoziladi (barkodlar bir-biriga qo'shilib ketmaydi), ro'yxat esa filtrlangan
   * holicha turadi — sotuv sahifasidagi bilan bir xil xatti-harakat.
   * </p>
   */
  onSearchEnter() {
    const query = this.searchText.trim();
    if (!query) {
      // Bo'sh input bilan Enter — filtrni tozalash. `searchQuery` ham bo'shatilmasa,
      // oldingi Enterdan qolgan so'rov qayta-qayta yuborilaverardi: input allaqachon
      // tozalangan, lekin serverga eski matn ketardi.
      this.searchQuery = '';
      this.loadedQuery = null;
      this.onSearch();
      return;
    }

    if (this.loadedQuery === query) {
      this.searchText = '';
      return;
    }
    this.loadProducts({ first: 0, rows: this.pageSize }, () => (this.searchText = ''));
  }

  reload() {
    this.loadProducts(this.lastLazyEvent);
  }

  // ── CRUD ─────────────────────────────────────────────────────────────────────

  // Mahsulot qo'shish va stok qo'shish — faqat OMBOR harakati. Pul tomoni (kassa,
  // yetkazuvchiga qarz, kirim hujjati) ataylab yo'q: u "Kirim" (Purchases) bo'limining
  // ishi. Ikkisini bitta oynada aralashtirish qoldiq tuzatishni ham pul harakatiga
  // aylantirib, hisobni chalkashtirardi.

  openCreate() {
    this.editingId = null;
    this.form = this.emptyForm();
    this.formStoreId.set(null);
    // Effekt keyingi mikrotaskda ishlaydi — oyna esa allaqachon ochiq bo'ladi.
    // Yagona do'kon shu yerda DARHOL qo'yiladi, aks holda kategoriya va o'lchov
    // birligi bir lahza bloklangan ko'rinib turardi.
    const onlyStore = this.formStore.single();
    if (onlyStore) this.onFormStoreChange(onlyStore.id);
    this.discountType = '%';
    this.discountInput = 0;
    this.markupPercent = 0;
    this.showNewCat = false;
    this.newCatName = '';
    this.showNewUnit = false;
    this.newUnitName = '';
    this.imageFile = null;
    this.imagePreview.set(null);
    this.imageError.set(null);
    this.originalImageUrl = null;
    this.removeExistingImage = false;
    this.variantMode.set(false);
    this.variantDrawerVisible = false;
    this.combos.set([]);
    this.dialogVisible = true;
  }

  openEdit(p: ProductDto) {
    this.editingId = p.id;
    this.formStoreId.set(p.storeId);
    this.form = {
      name: p.name,
      brand: p.brand ?? '',
      barcode: p.barcode,
      purchasePrice: p.purchasePrice,
      sellPrice: p.sellPrice,
      initialQuantity: p.stocks,
      storeId: p.storeId,
      categoryId: p.categoryId ?? null,
      unitOfMeasureId: p.unitOfMeasureId ?? null,
      isWeight: p.isWeight ?? false,
      sellCurrency: p.sellCurrency ?? this.currencySvc.defaultCode(),
      purchaseCurrency: p.purchaseCurrency ?? this.currencySvc.defaultCode(),
      adjustReason: '',
    };
    this.editingHasVariants = p.hasVariants ?? false;
    this.originalEditQuantity = p.stocks;
    this.variantAdjustReason = '';
    this.discountType = 'sum';
    this.discountInput = p.discount;
    // Formulani takrorlamaymiz — `onSellPriceChange` yagona manba (valyuta
    // tekshiruvi va aniqlik ham o'sha yerda). Ilgari bu yerda nusxasi turardi.
    this.onSellPriceChange();
    this.showNewCat = false;
    this.newCatName = '';
    this.showNewUnit = false;
    this.newUnitName = '';
    this.imageFile = null;
    this.imagePreview.set(p.imageUrl ?? null);
    this.imageError.set(null);
    this.originalImageUrl = p.imageUrl ?? null;
    this.removeExistingImage = false;
    this.dialogVisible = true;
  }

  closeDialog() {
    this.stopCamera();
    this.dialogVisible = false;
    this.imageFile = null;
    this.imagePreview.set(null);
    this.imageError.set(null);
    this.originalImageUrl = null;
    this.removeExistingImage = false;
    this.variantMode.set(false);
    this.variantDrawerVisible = false;
    this.combos.set([]);
    this.editingHasVariants = false;
  }

  // ── Mahsulot detali (view) ─────────────────────────────────────────────────
  detailVisible = false;
  detailProduct: ProductDto | null = null;

  openDetail(product: ProductDto): void {
    this.detailProduct = product;
    this.detailVisible = true;
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.detailProduct = null;
  }

  variantLabel(v: ProductVariantDto): string {
    return v.values.map((vv) => vv.value).join(' / ');
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.processImage(file);
  }

  async openCamera() {
    this.imageError.set(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      this.imageError.set("Bu brauzer kameradan rasm olishni qo'llab-quvvatlamaydi");
      return;
    }

    try {
      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      this.cameraOpen.set(true);

      // Video elementi @if bilan keyingi renderda yaratiladi.
      setTimeout(() => {
        const video = this.cameraPreview?.nativeElement;
        if (video && this.cameraStream) {
          video.srcObject = this.cameraStream;
          void video.play();
        }
      });
    } catch {
      this.stopCamera();
      this.imageError.set('Kameraga ruxsat berilmadi yoki kamera mavjud emas');
    }
  }

  capturePhoto() {
    const video = this.cameraPreview?.nativeElement;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      this.imageError.set("Kamera hali tayyor emas, biroz kutib qayta urinib ko'ring");
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.imageError.set('Rasmni olishda xatolik yuz berdi');
          return;
        }

        this.stopCamera();
        this.processImage(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  }

  stopCamera() {
    this.cameraStream?.getTracks().forEach((track) => track.stop());
    this.cameraStream = null;
    this.cameraOpen.set(false);
  }

  /** Yuborishdan oldingi maksimal o'lcham — bundan kattasi kichraytiriladi. */
  private static readonly IMAGE_MAX_PX = 1200;

  /**
   * XOM fayl uchun chegara — siqishdan OLDIN tekshiriladi.
   *
   * Siqishning o'zi 5 MB muammosini hal qiladi, lekin bu chegara boshqa narsadan
   * himoya qiladi: juda katta faylni brauzerga dekodlatish (100 MP surat) telefon
   * xotirasini yeb, tabni yiqitishi mumkin. 20 MB — mahsulot surati uchun katta
   * zaxira bilan yetadi.
   */
  private static readonly IMAGE_MAX_RAW_BYTES = 20 * 1024 * 1024;

  private processImage(file: File) {
    // Accept jpeg/jpg/png/webp; mobile cameras may report image/jpg
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type.toLowerCase())) {
      this.imageError.set('Faqat JPEG, PNG yoki WebP formatidagi rasm yuklanishi mumkin');
      return;
    }

    if (file.size > ProductsComponent.IMAGE_MAX_RAW_BYTES) {
      this.imageError.set(
        `Fayl juda katta (${(file.size / (1024 * 1024)).toFixed(0)} MB). ` +
          `Kichikroq rasm tanlang — 20 MB gacha.`,
      );
      return;
    }

    this.imageError.set(null);

    // Siqish canvas orqali: telefon surati 10–20 MB bo'lishi mumkin, serverga esa
    // 1200 px JPEG (~200–400 KB) ketadi.
    //
    // `createObjectURL`, `FileReader.readAsDataURL` EMAS: ikkinchisi xom faylni
    // base64 satrga aylantiradi va xotirada ~1.33 barobar joy egallaydi — 20 MB lik
    // surat 27 MB satr bo'lib, telefonda sezilarli muzlash berardi.
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const MAX = ProductsComponent.IMAGE_MAX_PX;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;

      // Canvas shaffof bo'lib yaratiladi, JPEG'da esa alfa kanali yo'q — shaffof
      // piksellar QORA bo'lib chiqadi. Shaffof fonli PNG logotip shu sababli qora
      // kvadratga aylanardi. Oq fon shuni to'g'rilaydi.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            this.imageError.set('Rasmni qayta ishlashda xatolik');
            return;
          }
          if (blob.size > 5 * 1024 * 1024) {
            this.imageError.set('Rasm hajmi juda katta, kichikroq rasm tanlang');
            return;
          }
          this.imageFile = new File([blob], file.name, { type: 'image/jpeg' });
          this.imagePreview.set(canvas.toDataURL('image/jpeg', 0.85));
        },
        'image/jpeg',
        0.85,
      );
    };

    // Kengaytmasi rasmnikiga o'xshagan, lekin aslida rasm bo'lmagan fayl —
    // busiz hech qanday xabar chiqmasdan jimgina yo'qolardi va foydalanuvchi
    // saqlagandan keyingina rasm yo'qligini bilardi.
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      this.imageError.set("Bu faylni rasm sifatida ochib bo'lmadi. Boshqa rasm tanlang.");
    };

    img.src = objectUrl;
  }

  removeImage() {
    this.imageFile = null;
    this.imagePreview.set(null);
    this.imageError.set(null);
    if (this.originalImageUrl) {
      this.removeExistingImage = true;
    }
  }

  // ── Discount / price helpers ──────────────────────────────────────────────────

  /**
   * Narxdan ustamani teskari hisoblaydi.
   *
   * Valyutalar har xil bo'lsa hisoblanmaydi — `4.42 $` xaridni `55000 so'm`
   * sotishga taqqoslash 1 243 900% ustama degan bema'ni natija berardi.
   */
  onSellPriceChange() {
    const p = this.form.purchasePrice || 0;
    const s = this.form.sellPrice || 0;
    if (!this.profitComparable || p <= 0 || s <= 0) {
      this.markupPercent = 0;
      return;
    }
    // Ustama foizi 2 xonagacha — avval 1 xona edi va `20.25%` → `20.3%` bo'lib,
    // narxga qaytarilganda boshqa raqam chiqardi.
    this.markupPercent = Math.round(((s - p) / p) * 10000) / 100;
  }

  onPurchasePriceChange() {
    this.onSellPriceChange();
  }

  /**
   * Ustamadan narxni hisoblaydi.
   *
   * `Math.round()` ISHLATILMAYDI: `0.02 $` ga 50% ustama `0.03 $` beradi, eski
   * kod esa uni `0 $` ga aylantirardi.
   */
  onMarkupChange() {
    const p = this.form.purchasePrice || 0;
    if (!this.profitComparable || p <= 0) return;
    const raw = p * (1 + (this.markupPercent || 0) / 100);
    this.form.sellPrice = this.currencySvc.roundPrice(raw, this.form.sellCurrency);
  }

  onDiscountTypeChange() {
    this.discountInput = 0;
  }

  /**
   * Backendga yuboriladigan chegirma summasi (dona narxida).
   *
   * Narx bilan bir xil aniqlikda yaxlitlanadi — `Math.round()` bilan `4.4234 $`
   * dan 10% chegirma `0 $` bo'lib ketardi.
   */
  private computeDiscountAmount(): number {
    const raw =
      this.discountType === 'sum'
        ? this.discountInput || 0
        : ((this.form.sellPrice || 0) * (this.discountInput || 0)) / 100;
    return this.currencySvc.roundPrice(Math.max(0, raw), this.form.sellCurrency);
  }

  /**
   * Qoldiqni TUZATISH sifatida saqlaydi (StockHistoryType.Adjustment).
   *
   * MUHIM: bu yerda ATAYLAB add-stock/write-off ishlatilmaydi. Ilgari shunday edi va
   * natijada tahrirdagi har bir qoldiq to'g'rilash haqiqiy kirim yoki ZARAR bo'lib
   * yozilardi — "10 ni xato 100 qildim, keyin qaytardim" holati analitikada 90 birlik
   * soxta zarar qoldirardi (WriteOff netProfit'dan ayiriladi). Adjustment moliyaviy
   * neytral: qoldiq to'g'rilanadi, tarixda ko'rinadi, foydaga tegmaydi.
   *
   * Haqiqiy tovar harakati uchun: kirim → "Mahsulot qo'shish" dialogi (Purchases),
   * chiqim → "Chiqim" bo'limi (write-off).
   */
  private adjustStockTo(
    productId: string,
    storeId: string,
    newQuantity: number,
    reason: string,
    expectedCurrentQuantity: number,
    productVariantId?: string,
  ) {
    const request: AdjustStockRequest = {
      productId,
      storeId,
      newQuantity,
      reason,
      expectedCurrentQuantity,
      ...(productVariantId && { productVariantId }),
    };
    return this.inventorySvc.adjustStock(request);
  }

  private changeEditedProductStock(productId: string, done: () => void): void {
    if (this.editingHasVariants || this.form.initialQuantity === this.originalEditQuantity) {
      done();
      return;
    }
    if (!this.form.storeId) {
      this.productSvc.showWarn(
        "Mahsulot ma'lumotlari saqlandi, lekin miqdor uchun do'kon aniqlanmadi",
      );
      done();
      return;
    }

    this.adjustStockTo(
      productId,
      this.form.storeId,
      this.form.initialQuantity,
      this.form.adjustReason.trim(),
      this.originalEditQuantity,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: done, error: (err) => this.productSvc.showUpdateError(err) });
  }

  // ── Inline category creation ──────────────────────────────────────────────────

  createCategoryInline() {
    if (!this.newCatName.trim()) return;
    // StoreType majburiy — kategoriya tanlangan do'kon turiga bog'lanadi.
    const storeTypeId = this.formStoreTypeId();
    if (!storeTypeId) {
      this.categorySvc.showError(this.translate.instant('PRODUCTS.FORM.SELECT_STORE_FIRST'));
      return;
    }
    this.savingCat.set(true);
    this.categorySvc
      .create(this.newCatName.trim(), storeTypeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const newId = res.data?.id;
          this.categorySvc
            .getAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((r) => {
              this.categories.set(r.data ?? []);
              if (newId) this.form.categoryId = newId;
              this.showNewCat = false;
              this.newCatName = '';
              this.savingCat.set(false);
            });
        },
        error: () => {
          this.savingCat.set(false);
        },
      });
  }

  // ── Inline unit creation ──────────────────────────────────────────────────────

  createUnitInline() {
    if (!this.newUnitName.trim()) return;
    // StoreType majburiy — birlik tanlangan do'kon turiga bog'lanadi.
    const storeTypeId = this.formStoreTypeId();
    if (!storeTypeId) {
      this.unitSvc.showError(this.translate.instant('PRODUCTS.FORM.SELECT_STORE_FIRST'));
      return;
    }
    this.savingUnit.set(true);
    this.unitSvc
      .create(this.newUnitName.trim(), storeTypeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const newId = res.data?.id;
          this.unitSvc
            .getAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((r) => {
              this.units.set(r.data ?? []);
              if (newId) this.form.unitOfMeasureId = newId;
              this.showNewUnit = false;
              this.newUnitName = '';
              this.savingUnit.set(false);
            });
        },
        error: () => {
          this.savingUnit.set(false);
        },
      });
  }

  // ── Save ──────────────────────────────────────────────────────────────────────

  save() {
    if (!this.form.name.trim()) {
      this.productSvc.showWarn('Nom majburiy');
      return;
    }

    // O'lchov birligi majburiy: usiz mahsulot "1 nima?" bo'lib qoladi — chekda,
    // yorliqda va qoldiqda birlik ko'rsatilmaydi. Tahrirda ham tekshiriladi,
    // aks holda mavjud mahsulotdan birlikni olib tashlab saqlash mumkin bo'lardi.
    if (!this.form.unitOfMeasureId) {
      this.productSvc.showWarn(this.translate.instant('PRODUCTS.FORM.UNIT_REQUIRED'));
      return;
    }

    const isVariantCreate = !this.isEdit && this.variantMode();

    // Kelish narxi har doim majburiy — foyda/marja hisoblari shunga tayanadi.
    if (this.form.purchasePrice <= 0) {
      this.productSvc.showWarn('Kelish narxi majburiy');
      return;
    }

    // Variantsiz mahsulot — sotish narxi majburiy.
    if (!isVariantCreate && this.form.sellPrice <= 0) {
      this.productSvc.showWarn('Sotish narxi majburiy');
      return;
    }

    // Variant rejimi yoqilgan, lekin kombinatsiya yo'q — ogohlantiramiz.
    if (isVariantCreate && this.combos().length === 0) {
      this.productSvc.showWarn("Variant qiymatlarini tanlang yoki variant rejimini o'chiring");
      return;
    }

    // ── Variantli yo'l ───────────────────────────────────────────────────────
    if (isVariantCreate && this.combos().length > 0) {
      // Har bir variant uchun sotish narxi bo'lishi shart: o'z narxi YOKI asos narx.
      const missingPrice = this.combos().some(
        (c) => (c.price == null || c.price <= 0) && this.form.sellPrice <= 0,
      );
      if (missingPrice) {
        this.productSvc.showWarn(
          'Asos sotish narxini kiriting yoki har bir variant narxini to`ldiring',
        );
        return;
      }
      this.saving.set(true);
      const discount = this.computeDiscountAmount();
      const req: CreateProductWithVariantsRequest = {
        name: this.form.name.trim(),
        brand: this.form.brand.trim() || null,
        sellPrice: this.form.sellPrice,
        purchasePrice: this.form.purchasePrice,
        sellCurrency: this.form.sellCurrency,
        purchaseCurrency: this.form.purchaseCurrency,
        discount,
        categoryId: this.form.categoryId,
        unitOfMeasureId: this.form.unitOfMeasureId,
        isWeight: this.form.isWeight,
        storeId: this.form.storeId,
        variants: this.combos().map((c) => ({
          valueIds: c.valueIds,
          quantity: c.quantity,
          sellPrice: c.price,
          purchasePrice: c.purchasePrice,
        })),
      };
      this.productSvc
        .createWithVariants(req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const newId = res.data?.id;
            if (newId && this.imageFile) {
              this.uploadImageThen(newId, () => {
                this.productSvc.showCreateSuccess();
                this.saving.set(false);
                this.closeDialog();
                this.loadProducts({ first: 0, rows: this.pageSize });
              });
            } else {
              this.productSvc.showCreateSuccess();
              this.saving.set(false);
              this.closeDialog();
              this.loadProducts({ first: 0, rows: this.pageSize });
            }
          },
          error: () => {
            this.saving.set(false);
          },
        });
      return;
    }

    // ── Oddiy yo'l ────────────────────────────────────────────────────────────
    if (!this.form.barcode.trim()) {
      this.productSvc.showWarn('Shtrix-kod majburiy');
      return;
    }
    // Tahrirda qoldiq o'zgartirilsa — sabab majburiy. Bu amal kirim/chiqim emas,
    // "tuzatish" (Adjustment) bo'lib yoziladi, shuning uchun izohsiz qolmasligi kerak.
    if (
      this.editingId &&
      !this.editingHasVariants &&
      this.form.initialQuantity !== this.originalEditQuantity &&
      !this.form.adjustReason.trim()
    ) {
      this.productSvc.showWarn(this.translate.instant('PRODUCTS.FORM.ADJUST_REASON_REQUIRED'));
      return;
    }
    this.saving.set(true);
    const discount = this.computeDiscountAmount();

    if (this.editingId) {
      const editId = this.editingId;
      const req: UpdateProductRequest = {
        name: this.form.name,
        brand: this.form.brand.trim() || null,
        barcode: this.form.barcode,
        purchasePrice: this.form.purchasePrice,
        sellPrice: this.form.sellPrice,
        discount,
        categoryId: this.form.categoryId,
        unitOfMeasureId: this.form.unitOfMeasureId,
        isWeight: this.form.isWeight,
        sellCurrency: this.form.sellCurrency,
        purchaseCurrency: this.form.purchaseCurrency,
      };
      this.productSvc
        .update(editId, req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            const finish = () => {
              if (this.imageFile) {
                this.uploadImageThen(editId, () => {
                  this.productSvc.showUpdateSuccess();
                  this.saving.set(false);
                  this.closeDialog();
                  this.reload();
                });
              } else if (this.removeExistingImage) {
                this.productSvc
                  .deleteImage(editId)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: () => {
                      this.productSvc.showUpdateSuccess();
                      this.saving.set(false);
                      this.closeDialog();
                      this.reload();
                    },
                    error: () => {
                      this.productSvc.showWarn("Ma'lumot saqlandi, lekin rasm o'chirilmadi");
                      this.saving.set(false);
                      this.closeDialog();
                      this.reload();
                    },
                  });
              } else {
                this.productSvc.showUpdateSuccess();
                this.saving.set(false);
                this.closeDialog();
                this.reload();
              }
            };
            this.changeEditedProductStock(editId, finish);
          },
          error: () => {
            this.saving.set(false);
          },
        });
    } else {
      if (this.form.initialQuantity > 0 && !this.form.storeId) {
        this.productSvc.showWarn("Boshlang'ich stok qo'shish uchun do'kon tanlang");
        this.saving.set(false);
        return;
      }

      const req: CreateProductRequest = {
        name: this.form.name,
        brand: this.form.brand.trim() || null,
        barcode: this.form.barcode,
        purchasePrice: this.form.purchasePrice,
        sellPrice: this.form.sellPrice,
        discount,
        initialQuantity: this.form.initialQuantity,
        storeId: this.form.storeId,
        categoryId: this.form.categoryId,
        unitOfMeasureId: this.form.unitOfMeasureId,
        isWeight: this.form.isWeight,
        sellCurrency: this.form.sellCurrency,
        purchaseCurrency: this.form.purchaseCurrency,
      };
      this.productSvc
        .create(req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const newId = res.data?.id;
            if (newId && this.imageFile) {
              this.uploadImageThen(newId, () => {
                this.productSvc.showCreateSuccess();
                this.saving.set(false);
                this.closeDialog();
                this.loadProducts({ first: 0, rows: this.pageSize });
              });
            } else {
              this.productSvc.showCreateSuccess();
              this.saving.set(false);
              this.closeDialog();
              this.loadProducts({ first: 0, rows: this.pageSize });
            }
          },
          error: () => {
            this.saving.set(false);
          },
        });
    }
  }

  private uploadImageThen(productId: string, done: () => void) {
    if (!this.imageFile) {
      done();
      return;
    }
    const file = this.imageFile;
    this.imageUploading.set(true);
    this.productSvc
      .uploadImage(productId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.imageUploading.set(false);
          done();
        },
        error: () => {
          this.imageUploading.set(false);
          this.productSvc.showWarn("Ma'lumot saqlandi, lekin rasm yuklanmadi");
          done();
        },
      });
  }

  // ── Transfer ──────────────────────────────────────────────────────────────────

  openTransfer() {
    this.transferForm = this.emptyTransferForm();
    this.transferProductId.set(null);
    this.transferFromStoreId.set(null);
    this.transferToStoreId.set(null);
    this.transferStoreProducts.set([]);
    this.transferVariants.set([]);
    this.transferDialogVisible = true;
  }

  closeTransfer() {
    this.transferDialogVisible = false;
    this.transferProductId.set(null);
    this.transferFromStoreId.set(null);
    this.transferToStoreId.set(null);
    this.transferStoreProducts.set([]);
    this.transferVariants.set([]);
  }

  onTransferFromStoreChange(storeId: string | null) {
    this.transferForm.fromStoreId = storeId;
    this.transferForm.productId = null;
    this.transferForm.productVariantId = null;
    this.transferForm.toStoreId = null;
    this.transferFromStoreId.set(storeId);
    this.transferProductId.set(null);
    this.transferToStoreId.set(null);
    this.transferStoreProducts.set([]);
    this.transferVariants.set([]);
    if (!storeId) return;
    this.transferStoreProductsLoading.set(true);
    this.productSvc
      .getAllLite(storeId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.transferStoreProductsLoading.set(false)),
      )
      .subscribe({ next: (res) => this.transferStoreProducts.set(res.data ?? []) });
  }

  onTransferProductChange(productId: string | null) {
    this.transferForm.productId = productId;
    this.transferForm.productVariantId = null;
    this.transferProductId.set(productId);
    this.transferVariants.set([]);
    if (!productId) return;
    this.transferVariantsLoading.set(true);
    this.productSvc
      .getVariantMatrix(productId, this.transferForm.fromStoreId ?? undefined)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.transferVariantsLoading.set(false)),
      )
      .subscribe({
        next: (res) => {
          const m = res.data;
          if (m && m.attributes.length > 0 && m.variants.length > 0) {
            this.transferVariants.set(m.variants);
          }
        },
        error: () => {
          /* empty */
        },
      });
  }

  onTransferToStoreChange(storeId: string | null) {
    this.transferForm.toStoreId = storeId;
    this.transferToStoreId.set(storeId);
  }

  saveTransfer() {
    const f = this.transferForm;
    if (!f.productId || !f.fromStoreId || !f.toStoreId || f.quantity <= 0) {
      this.inventorySvc.showWarn("Barcha majburiy maydonlarni to'ldiring");
      return;
    }
    this.transferSaving.set(true);
    const req: TransferRequest = {
      productId: f.productId,
      fromStoreId: f.fromStoreId,
      toStoreId: f.toStoreId,
      quantity: f.quantity,
      description: f.description,
      productVariantId: f.productVariantId ?? null,
    };
    this.inventorySvc
      .transfer(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.inventorySvc.showSuccess('Transfer muvaffaqiyatli amalga oshirildi');
          this.transferSaving.set(false);
          this.closeTransfer();
          this.reload();
        },
        error: () => {
          this.transferSaving.set(false);
        },
      });
  }

  // ── Stock ─────────────────────────────────────────────────────────────────────

  openStock(product?: ProductDto) {
    this.stockForm = this.emptyStockForm();
    this.stockAdvanced = false;
    this.stockProductId.set(null);
    this.stockVariants.set([]);
    this.stockDialogVisible = true;
    if (this.allProducts().length === 0) {
      this.allProductsLoading.set(true);
      this.productSvc
        .getAllLite()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.allProducts.set(res.data ?? []);
            this.allProductsLoading.set(false);
          },
          error: () => {
            this.allProductsLoading.set(false);
          },
        });
    }
    if (product) {
      const storeTypeId =
        this.categories().find((c) => c.id === product.categoryId)?.storeTypeId ?? null;
      if (!this.allProducts().find((p) => p.id === product.id)) {
        this.allProducts.update((list) => [
          {
            id: product.id,
            name: product.name,
            barcode: product.barcode,
            storeTypeId,
            hasVariants: product.hasVariants ?? false,
            purchasePrice: product.purchasePrice,
            sellPrice: product.sellPrice,
            sellCurrency: product.sellCurrency,
            purchaseCurrency: product.purchaseCurrency,
          },
          ...list,
        ]);
      }
      this.onStockProductChange(product.id);
    }
  }

  closeStock() {
    this.stockDialogVisible = false;
    this.stockProductId.set(null);
    this.stockVariants.set([]);
  }

  onStockProductChange(productId: string | null) {
    this.stockForm.productId = productId;
    this.stockProductId.set(productId);
    this.stockForm.storeId = null;
    this.stockForm.productVariantId = null;
    this.stockVariants.set([]);
    if (!productId) return;

    // Mahsulotga mos do'kon bitta bo'lsa tanlagich ko'rsatilmaydi — qiymatni shu
    // yerda qo'yamiz. `onStockStoreChange` variantlarni AYNAN shu do'kon qoldig'i
    // bilan yuklaydi, ya'ni quyidagi umumiy yuklashdan aniqroq; ikkisi birga
    // chaqirilsa esa ikkita so'rov poyga qilardi.
    const onlyStore = this.stockStore.single();
    if (onlyStore) {
      this.onStockStoreChange(onlyStore.id);
      return;
    }

    this.stockVariantsLoading.set(true);
    this.productSvc
      .getVariantMatrix(productId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.stockVariantsLoading.set(false)),
      )
      .subscribe({
        next: (res) => {
          const m = res.data;
          if (m && m.attributes.length > 0 && m.variants.length > 0) {
            this.stockVariants.set(m.variants);
          }
        },
        error: () => {
          /* empty */
        },
      });
  }

  onStockStoreChange(storeId: string | null) {
    this.stockForm.storeId = storeId;
    this.stockForm.productVariantId = null;
    this.stockVariants.set([]);
    const pid = this.stockForm.productId;
    if (!pid || !storeId) return;
    this.stockVariantsLoading.set(true);
    this.productSvc
      .getVariantMatrix(pid, storeId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.stockVariantsLoading.set(false)),
      )
      .subscribe({
        next: (res) => {
          const m = res.data;
          if (m && m.attributes.length > 0 && m.variants.length > 0) {
            this.stockVariants.set(m.variants);
          }
        },
        error: () => {
          /* empty */
        },
      });
  }

  onStockBarcodeDetected(barcode: string) {
    const found = this.allProducts().find((p) => p.barcode === barcode);
    if (found) {
      this.onStockProductChange(found.id);
      this.productSvc.showSuccess(`Topildi: ${found.name}`);
    } else {
      this.productSvc
        .getByBarcode(barcode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (p) => {
            if (p) {
              if (!this.allProducts().find((x) => x.id === p.id)) {
                this.allProducts.update((list) => [
                  {
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    storeTypeId: null,
                    hasVariants: p.hasVariants ?? false,
                    purchasePrice: p.purchasePrice,
                    sellPrice: p.sellPrice,
                    sellCurrency: p.sellCurrency,
                    purchaseCurrency: p.purchaseCurrency,
                  },
                  ...list,
                ]);
              }
              this.onStockProductChange(p.id);
              this.productSvc.showSuccess(`Topildi: ${p.name}`);
            } else {
              this.productSvc.showWarn(`Barkod bo'yicha mahsulot topilmadi: ${barcode}`);
            }
          },
        });
    }
  }

  onSearchBarcodeDetected(barcode: string) {
    this.searchText = barcode;
    this.searchQuery = barcode;
    this.onSearch();
  }

  onFormBarcodeDetected(barcode: string) {
    this.form.barcode = barcode;
  }

  onTransferBarcodeDetected(barcode: string) {
    const found = this.allProducts().find((p) => p.barcode === barcode);
    if (found) {
      this.transferForm.productId = found.id;
      this.productSvc.showSuccess(`Topildi: ${found.name}`);
    } else {
      this.productSvc
        .getByBarcode(barcode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (p) => {
            if (p) {
              this.transferForm.productId = p.id;
              if (!this.allProducts().find((x) => x.id === p.id)) {
                this.allProducts.update((list) => [
                  {
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    storeTypeId: null,
                    hasVariants: p.hasVariants ?? false,
                    purchasePrice: p.purchasePrice,
                    sellPrice: p.sellPrice,
                    sellCurrency: p.sellCurrency,
                    purchaseCurrency: p.purchaseCurrency,
                  },
                  ...list,
                ]);
              }
              this.productSvc.showSuccess(`Topildi: ${p.name}`);
            } else {
              this.productSvc.showWarn(`Barkod bo'yicha mahsulot topilmadi: ${barcode}`);
            }
          },
        });
    }
  }

  saveStock() {
    const f = this.stockForm;
    // Tanlagich yashirilgan bo'lsa qiymat mahsulot tanlanganda qo'yiladi. Do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira — yashirin maydonni foydalanuvchi
    // to'ldira olmasdi.
    f.storeId ??= this.stockStore.single()?.id ?? null;

    if (!f.productId || !f.storeId || f.quantity <= 0) {
      this.inventorySvc.showWarn("Mahsulot, do'kon va miqdor majburiy");
      return;
    }
    if (this.stockVariants().length > 0 && !f.productVariantId) {
      this.inventorySvc.showWarn('Variantni tanlang');
      return;
    }
    // FAQAT ombor harakati: kirim hujjati yaratilmaydi, kassa va qarzlar qimirlamaydi.
    // Pulli kirim "Kirim" (Purchases) bo'limidan kiritiladi — u yerda yetkazib
    // beruvchi, to'lov usuli va to'langan summa to'liq so'raladi.
    this.stockSaving.set(true);
    this.inventorySvc
      .addStock({
        productId: f.productId,
        storeId: f.storeId,
        quantity: f.quantity,
        productVariantId: f.productVariantId,
        newPurchasePrice: this.stockAdvanced ? (f.newPurchasePrice ?? undefined) : undefined,
        newSellPrice: this.stockAdvanced ? (f.newSellPrice ?? undefined) : undefined,
        description: f.description || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.inventorySvc.showSuccess(this.translate.instant('PRODUCTS.STOCK_DIALOG.SAVED'));
          this.stockSaving.set(false);
          this.closeStock();
          this.reload();
        },
        error: () => {
          this.stockSaving.set(false);
        },
      });
  }

  confirmDelete(p: ProductDto) {
    this.confirmSvc.confirm({
      message: this.translate.instant('PRODUCTS.DELETE_CONFIRM.MESSAGE', { name: p.name }),
      header: this.translate.instant('PRODUCTS.DELETE_CONFIRM.HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('PRODUCTS.DELETE_CONFIRM.ACCEPT'),
      rejectLabel: this.translate.instant('PRODUCTS.DELETE_CONFIRM.REJECT'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.productSvc
          .delete(p.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.productSvc.showDeleteSuccess();
              this.reload();
            },
          });
      },
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  getStoreName(storeId: string | null): string {
    if (!storeId) return '—';
    return this.stores().find((s) => s.id === storeId)?.name ?? storeId;
  }

  generatingBarcode = signal(false);

  generateBarcode(): void {
    this.generatingBarcode.set(true);
    this.productSvc
      .generateBarcode()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.generatingBarcode.set(false)),
      )
      .subscribe({
        next: (barcode) => {
          this.form.barcode = barcode;
        },
        error: () => {
          this.form.barcode = this.generateEan13Fallback();
          this.productSvc.showWarn("Server bilan bog'lanib bo'lmadi, lokal barcode yaratildi");
        },
      });
  }

  private generateEan13Fallback(): string {
    const prefix = (200 + Math.floor(Math.random() * 100)).toString();
    let digits = prefix;
    for (let i = 0; i < 9; i++) digits += Math.floor(Math.random() * 10).toString();
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(digits[i]) * (i % 2 === 0 ? 1 : 3);
    return digits + ((10 - (sum % 10)) % 10);
  }

  /** 478NNNNNNNNNC → PLU (5 xona, taroziga dasturlash uchun) */
  getWeightPlu(barcode: string): string {
    if (!barcode || barcode.length !== 13) return '?????';
    const num = parseInt(barcode.substring(3, 12), 10);
    return (num % 100000).toString().padStart(5, '0');
  }

  private emptyForm(): ProductForm {
    return {
      name: '',
      brand: '',
      barcode: '',
      purchasePrice: 0,
      sellPrice: 0,
      initialQuantity: 0,
      storeId: null,
      categoryId: null,
      unitOfMeasureId: null,
      isWeight: false,
      sellCurrency: this.currencySvc.defaultCode(),
      purchaseCurrency: this.currencySvc.defaultCode(),
      adjustReason: '',
    };
  }

  private emptyTransferForm() {
    return {
      productId: null as string | null,
      fromStoreId: null as string | null,
      toStoreId: null as string | null,
      quantity: 1,
      description: '',
      productVariantId: null as string | null,
    };
  }

  private emptyStockForm() {
    return {
      productId: null as string | null,
      storeId: null as string | null,
      quantity: 1,
      productVariantId: null as string | null,
      newPurchasePrice: null as number | null,
      newSellPrice: null as number | null,
      description: '',
    };
  }
}
